// Keep these client-side checks aligned with backend/accounts/input_cleaning.py.
// The backend remains authoritative because browser validation can be bypassed.
export const EMAIL_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._%+-]*@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;
export const NAME_PATTERN = /^[A-Za-z\u00C0-\u017F]+(?:[ '.-][A-Za-z\u00C0-\u017F]+)*$/;
export const USERNAME_PATTERN = /^[A-Za-z0-9._-]{3,30}$/;

const RESERVED_USERNAMES = new Set(['admin', 'root', 'support', 'null', 'undefined']);
const APPROVED_SYMBOLS = /[!@#$%^&*()\-_+.]/;

export function validateName(value, label) {
  const cleaned = value.trim().replace(/\s+/g, ' ');
  if (!cleaned) return `Enter your ${label}.`;
  if (cleaned.length > 50)
    return `${label[0].toUpperCase() + label.slice(1)} must be 50 characters or fewer.`;
  if (!NAME_PATTERN.test(cleaned))
    return `${label[0].toUpperCase() + label.slice(1)} contains unsupported characters or separators.`;
  return null;
}

export function validateEmail(value) {
  if (!value.trim()) return 'Enter your email address.';
  if (value.trim().length > 254 || !EMAIL_PATTERN.test(value.trim()))
    return 'Enter a valid email address with a complete domain.';
  return null;
}

export function validatePhoneNumber(value) {
  const compact = value.trim().replace(/[\s().-]/g, '');
  if (!compact) return null;
  const normalized = /^\d{10}$/.test(compact) ? `+1${compact}` : compact;
  if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
    return 'Enter a valid phone number, including the country code when outside the US.';
  }
  return null;
}

export function validateUsername(value) {
  const cleaned = value.trim();
  if (!cleaned) return 'Enter a username.';
  if (!USERNAME_PATTERN.test(cleaned))
    return 'Username must be 3 to 30 characters using letters, numbers, periods, dashes, or underscores.';
  if (RESERVED_USERNAMES.has(cleaned.toLowerCase())) return 'Choose a different username.';
  return null;
}

const countMatches = (value, pattern) => (value.match(pattern) || []).length;

// Same order and wording as PASSWORD_RULES in backend/accounts/input_cleaning.py.
const PASSWORD_RULES = [
  [(value) => value.length >= 12, 'Password must be at least 12 characters long.'],
  [
    (value) => countMatches(value, /[a-z]/g) >= 3,
    'Password must contain at least three lowercase letters.',
  ],
  [
    (value) => countMatches(value, /[A-Z]/g) >= 2,
    'Password must contain at least two uppercase letters.',
  ],
  [(value) => countMatches(value, /[0-9]/g) >= 2, 'Password must contain at least two numbers.'],
  [(value) => APPROVED_SYMBOLS.test(value), 'Password must contain at least one approved symbol.'],
];

/** Every requirement this password does not meet, in display order; met ones are left out. */
export function unmetPasswordRequirements(password, { username = '', email = '' } = {}) {
  const problems = PASSWORD_RULES.filter(([check]) => !check(password)).map(
    ([, message]) => message,
  );
  const foldedPassword = password.toLowerCase();
  if (
    [username, email].some(
      (identity) => identity.trim() && foldedPassword.includes(identity.trim().toLowerCase()),
    )
  ) {
    problems.push('Password must not contain your username or email address.');
  }
  return problems;
}

export function validatePassword(password, identity) {
  if (!password) return 'Enter a password.';
  const problems = unmetPasswordRequirements(password, identity);
  return problems.length ? problems.join(' ') : null;
}
